import { DAY_KEYS, type WeeklyHours } from '@/types/restaurant';
import { localParts } from '@/lib/hours';

export type { WeeklyHours };
export type Period = 'lunch' | 'dinner' | 'closed';
export const SPLIT_HOUR = 16;

// DF-0330 — default when no restaurant timezone is supplied: the RUNNING
// environment's own tz, same rationale as checkoutSlots.ts's SYSTEM_TZ.
const SYSTEM_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function currentPeriod(
  now: Date,
  hours: WeeklyHours | null | undefined,
  tz: string = SYSTEM_TZ,
): Period {
  if (!hours) return 'closed';
  // DF-0330 — the current service is the RESTAURANT's, not the browser's.
  const p = localParts(now, tz);
  const todays = hours[DAY_KEYS[p.weekday]] ?? [];
  const minutes = p.hh * 60 + p.mm;
  const inside = todays.some(({ open, close }) => {
    const o = toMinutes(open);
    const c = toMinutes(close);
    return minutes >= o && minutes < c;
  });
  if (!inside) return 'closed';
  return p.hh < SPLIT_HOUR ? 'lunch' : 'dinner';
}

/** Service period of a concrete same-day slot (`"HH:MM"`). Checkout slots are
 *  only emitted inside an open range, so the answer is always lunch or dinner —
 *  same 16:00 split as `currentPeriod`. */
export function slotPeriod(hhmm: string): Exclude<Period, 'closed'> {
  return toMinutes(hhmm) < SPLIT_HOUR * 60 ? 'lunch' : 'dinner';
}
